import { createReducer, on } from '@ngrx/store';
import {
  AgentSection,
  CalculationAddOns,
  DiscountCodeResponse,
  PersonsWith,
  PremiumCalculationApi,
  QuotationResponse,
} from 'src/app/model/offer';
import { SendStaticAttachmentsResponse } from 'src/app/model/send-static-attachments.model';
import { SwitcherStates } from '../../component/offer-content/offer-content.component';
import { ApiState } from '../../model/api-state.model';
import AppError from '../../model/app-error';
import { BlockValidatorResponse, KnfLimitCheckPayload } from '../../model/async-validation.model';
import { CalculationToClientConfirmResponse } from '../../model/calculation-to-client-confirm.model';
import { ConclusionType } from '../../model/conclusion-type.model';
import { ConfirmResponse } from '../../model/confirm.model';
import { ProcessType } from '../../model/process-type.model';
import { FormOfPayment, PurchaseAgentResponse } from '../../model/purchase-agent.model';
import {
  AgentSaveCalculationPayload,
  LoadPolicyDataResponse,
  SaveCalculationPayload,
  SaveCalculationResponse,
} from '../../model/save-calculation.model';
import processActions from './process.actions';

export { processActions as actions };

export * from './process.selectors';

const INITIAL_VALUE = -1;
const INCREMENT_VALUE = 1;

export interface State {
  step: number;
  kind: ProcessType | null;
  isSuccess: boolean;
  conclusion: ConclusionType | null;
  savedCalculationState: ApiState;
  savedCalculationPayload: SaveCalculationPayload | AgentSaveCalculationPayload | null;
  savedCalculation: SaveCalculationResponse | null;
  savedCalculationError: AppError | null;
  savedOfferCalculationState: ApiState;
  savedOfferCalculation: SaveCalculationResponse | null;
  savedOfferCalculationError: AppError | null;
  confirmState: ApiState;
  confirm: ConfirmResponse | null;
  selectedPackage: string;
  selectedOfferValue: number | null;
  selectedOfferValueBeforeDiscounts: number | null;
  selectedAddons: CalculationAddOns | null;
  conclusionState: ApiState;
  conclusionData: PurchaseAgentResponse | null;
  conclusionError: AppError | null;
  navigationError: string | null;
  navigationErrorPreserve: boolean;
  formOfPayment: FormOfPayment | null;
  sendStaticAttachmentsState: ApiState;
  sendStaticAttachmentsData: SendStaticAttachmentsResponse | null;
  sendStaticAttachmentsError: AppError | null;
  calculationToClientConfirmState: ApiState;
  calculationToClientConfirmData: CalculationToClientConfirmResponse | null;
  calculationToClientConfirmError: AppError | null;
  quotationId: string | null;
  quotationState: ApiState;
  quotationData: QuotationResponse | null;
  quotationError: AppError | null;
  discountCodeState: ApiState;
  discountCodeData: DiscountCodeResponse | null;
  discountCodeError: AppError | null;
  discountCode: string | null;
  premiumCalculationState: Record<string, ApiState>;
  premiumCalculationData: Record<string, PremiumCalculationApi>;
  premiumCalculationError: AppError | null;
  customizeAlcoholIncluded: PersonsWith[] | null;
  customizeForTheActiveIncluded: PersonsWith[] | null;
  agentSection: AgentSection;
  packageAddons: SwitcherStates;
  usePolicyDataState: ApiState;
  usePolicyData: LoadPolicyDataResponse | null;
  usePolicyDataError: AppError | null;
  sendBackOfficeData: boolean | null;
  isOfferPriceBoxVisible: boolean;
  knfLimitState: ApiState;
  knfLimitPayload: KnfLimitCheckPayload | null;
  knfLimit: BlockValidatorResponse | null;
  knfLimitError: AppError | null;
  blockListCheckState: ApiState;
  blockListCheck: BlockValidatorResponse | null;
  blockListCheckError: AppError | null;
}

export const initialState: State = {
  step: INITIAL_VALUE,
  kind: null,
  isSuccess: false,
  conclusion: null,
  conclusionError: null,
  savedCalculationState: ApiState.idle,
  savedCalculation: null,
  savedCalculationPayload: null,
  savedCalculationError: null,
  savedOfferCalculationState: ApiState.idle,
  savedOfferCalculation: null,
  savedOfferCalculationError: null,
  confirmState: ApiState.idle,
  confirm: null,
  selectedOfferValue: null,
  selectedOfferValueBeforeDiscounts: null,
  selectedPackage: '',
  selectedAddons: null,
  conclusionState: ApiState.idle,
  conclusionData: null,
  navigationError: null,
  navigationErrorPreserve: false,
  formOfPayment: null,
  sendStaticAttachmentsState: ApiState.idle,
  sendStaticAttachmentsData: null,
  sendStaticAttachmentsError: null,
  calculationToClientConfirmState: ApiState.idle,
  calculationToClientConfirmData: null,
  calculationToClientConfirmError: null,
  quotationId: null,
  quotationState: ApiState.idle,
  quotationData: null,
  quotationError: null,
  discountCodeState: ApiState.idle,
  discountCodeData: null,
  discountCodeError: null,
  discountCode: null,
  premiumCalculationState: {},
  premiumCalculationData: {},
  premiumCalculationError: null,
  customizeAlcoholIncluded: null,
  customizeForTheActiveIncluded: null,
  agentSection: {
    premiumPercentIncrease: null,
  },
  packageAddons: {
    state: {},
  },
  usePolicyDataState: ApiState.idle,
  usePolicyData: null,
  usePolicyDataError: null,
  sendBackOfficeData: null,
  isOfferPriceBoxVisible: true,

  knfLimitState: ApiState.idle,
  knfLimitPayload: null,
  knfLimit: null,
  knfLimitError: null,
  blockListCheckState: ApiState.idle,
  blockListCheck: null,
  blockListCheckError: null,
};

export const reducer = createReducer(
  initialState,
  on(processActions.reset, () => initialState),
  on(processActions.nextStep, (state) => ({ ...state, step: state.step + INCREMENT_VALUE })),
  on(processActions.prevStep, (state) => ({ ...state, count: state.step - INCREMENT_VALUE })),
  on(processActions.goToStep, (state, { step }) => ({ ...state, step })),
  on(processActions.setStep, (state, { step }) => ({
    ...state,
    step,
    navigationError: state.navigationErrorPreserve ? state.navigationError : null,
  })),
  on(processActions.setSendBackOfficeData, (state, { sendBackOfficeData }) => ({
    ...state,
    sendBackOfficeData,
  })),
  on(processActions.setKind, (state, { kind }) => ({ ...state, kind })),
  on(processActions.setSuccess, (state, { success }) => ({ ...state, isSuccess: success })),
  on(processActions.setConclusion, (state, { conclusion }) => ({ ...state, conclusion })),
  on(processActions.saveCalculationStart, (state, { payload }) => ({
    ...state,
    savedCalculationState: ApiState.pending,
    savedCalculationError: null,
    savedCalculationPayload: payload,
  })),
  on(processActions.saveCalculationSuccess, (state, { success }) => ({
    ...state,
    savedCalculationState: ApiState.success,
    savedCalculation: success,
    savedCalculationError: null,
  })),
  on(processActions.saveCalculationError, (state, { error }) => ({
    ...state,
    savedCalculationState: ApiState.error,
    savedCalculation: null,
    savedCalculationError: error,
    savedCalculationPayload: null,
  })),
  on(processActions.saveOfferCalculationStart, (state) => ({
    ...state,
    savedOfferCalculationState: ApiState.pending,
    savedOfferCalculationError: null,
  })),
  on(processActions.saveOfferCalculationSuccess, (state, { success }) => ({
    ...state,
    savedOfferCalculationState: ApiState.success,
    savedOfferCalculation: success,
    savedOfferCalculationError: null,
  })),
  on(processActions.saveOfferCalculationError, (state, { error }) => ({
    ...state,
    savedOfferCalculationState: ApiState.error,
    savedOfferCalculation: null,
    savedOfferCalculationError: error,
  })),
  on(processActions.saveOfferCalculationReset, (state) => ({
    ...state,
    savedOfferCalculationState: ApiState.idle,
    savedOfferCalculation: null,
    savedOfferCalculationError: null,
  })),
  on(processActions.sendStaticAttachmentsStart, (state) => ({
    ...state,
    sendStaticAttachmentsState: ApiState.pending,
    sendStaticAttachmentsData: null,
    sendStaticAttachmentsError: null,
  })),
  on(processActions.sendStaticAttachmentsSuccess, (state, { success }) => ({
    ...state,
    sendStaticAttachmentsState: ApiState.success,
    sendStaticAttachmentsData: success,
    sendStaticAttachmentsError: null,
  })),
  on(processActions.sendStaticAttachmentsError, (state, { error }) => ({
    ...state,
    sendStaticAttachmentsState: ApiState.error,
    sendStaticAttachmentsData: null,
    sendStaticAttachmentsError: error,
  })),
  on(processActions.sendStaticAttachmentsReset, (state) => ({
    ...state,
    sendStaticAttachmentsState: ApiState.idle,
    sendStaticAttachmentsData: null,
    sendStaticAttachmentsError: null,
  })),
  on(processActions.confirmStart, (state) => ({ ...state, confirmState: ApiState.pending })),
  on(processActions.confirmSuccess, (state, { success }) => ({
    ...state,
    confirmState: ApiState.success,
    confirm: success,
  })),
  on(processActions.confirmError, (state, { error }) => ({
    ...state,
    confirmState: ApiState.error,
    confirm: error,
  })),
  on(processActions.selectPackage, (state, { packageName }) => ({ ...state, selectedPackage: packageName })),
  on(processActions.setOfferValue, (state, { offerValue }) => ({ ...state, selectedOfferValue: offerValue })),
  on(processActions.setOfferValueBeforeDiscounts, (state, { offerValueBeforeDiscounts }) => ({
    ...state,
    selectedOfferValueBeforeDiscounts: offerValueBeforeDiscounts,
  })),
  on(processActions.setAddons, (state, { addOns }) => ({ ...state, selectedAddons: addOns })),
  on(processActions.conclusionStart, (state) => ({ ...state, conclusionState: ApiState.pending, conclusionError: null })),
  on(processActions.conclusionSuccess, (state, { success }) => ({
    ...state,
    conclusionState: ApiState.success,
    conclusionData: success,
    conclusionError: null,
  })),
  on(processActions.conclusionError, (state, { error }) => ({ ...state, conclusionState: ApiState.error, conclusionError: error })),
  on(processActions.pushNavigationError, (state, { error, preserve }) => ({
    ...state,
    navigationError: error,
    navigationErrorPreserve: preserve || false,
  })),
  on(processActions.resetNavigationError, (state) => ({ ...state, navigationError: null })),
  on(processActions.setFormOfPayment, (state, { formOfPayment }) => ({ ...state, formOfPayment })),
  on(processActions.calculationToClientConfirmStart, (state) => ({
    ...state,
    calculationToClientConfirmState: ApiState.pending,
    calculationToClientConfirmData: null,
    calculationToClientConfirmError: null,
  })),
  on(processActions.calculationToClientConfirmSuccess, (state, { success }) => ({
    ...state,
    calculationToClientConfirmState: ApiState.success,
    calculationToClientConfirmData: success,
    calculationToClientConfirmError: null,
  })),
  on(processActions.calculationToClientConfirmError, (state, { error }) => ({
    ...state,
    calculationToClientConfirmState: ApiState.error,
    calculationToClientConfirmData: null,
    calculationToClientConfirmError: error,
  })),
  on(processActions.fetchQuotationStart, (state) => ({
    ...state,
    quotationState: ApiState.pending,
    quotationError: null,
  })),
  on(processActions.fetchQuotationSuccess, (state, { data }) => ({
    ...state,
    quotationState: ApiState.success,
    quotationData: data,
    quotationError: null,
  })),
  on(processActions.fetchQuotationError, (state, { error }) => ({
    ...state,
    quotationState: ApiState.error,
    quotationData: null,
    quotationError: error,
    selectedPackage: '',
  })),
  on(processActions.fetchDiscountCodeStart, (state, { discountCode }) => ({
    ...state,
    discountCodeState: ApiState.pending,
    discountCodeData: null,
    discountCode,
    discountCodeError: null,
  })),
  on(processActions.fetchDiscountCodeSuccess, (state, { data }) => ({
    ...state,
    discountCodeState: ApiState.success,
    discountCodeData: data,
    discountCodeError: null,
  })),
  on(processActions.fetchDiscountCodeError, (state, { error }) => ({
    ...state,
    discountCodeState: ApiState.error,
    discountCodeData: null,
    discountCodeError: error,
  })),
  on(processActions.removeDiscountCode, (state) => ({
    ...state,
    discountCodeState: ApiState.idle,
    discountCodeData: null,
    discountCodeError: null,
    discountCode: null,
  })),
  on(
    processActions.fetchPremiumCalculationStart,
    (
      state,
      {
        body: {
          selectedOffert: { packageCodeName },
        },
      },
    ) => ({
      ...state,
      premiumCalculationState: { ...state.premiumCalculationState, [packageCodeName]: ApiState.pending },
    }),
  ),
  on(processActions.fetchPremiumCalculationSuccess, (state, { data, packageName }) => ({
    ...state,
    premiumCalculationState: { ...state.premiumCalculationState, [packageName]: ApiState.success },
    premiumCalculationData: { ...state.premiumCalculationData, [packageName]: data },
  })),
  on(processActions.fetchPremiumCalculationError, (state, { packageName }) => ({
    ...state,
    premiumCalculationState: { ...state.premiumCalculationState, [packageName]: ApiState.error },
    premiumCalculationData: {},
    customizeAlcoholIncluded: null,
  })),
  on(processActions.setCustomizeAlcoholIncluded, (state, { customizeAlcoholIncluded }) => ({ ...state, customizeAlcoholIncluded })),
  on(processActions.setCustomizeForTheActiveIncluded, (state, { customizeForTheActiveIncluded }) => ({
    ...state,
    customizeForTheActiveIncluded,
  })),
  on(processActions.setAgentSectionPremiumPercentIncrease, (state, { premiumPercentIncrease }) => ({
    ...state,
    agentSection: { ...state.agentSection, premiumPercentIncrease },
  })),
  on(processActions.setAgentSectionPremiumPercentIncrease, (state, { premiumPercentIncrease }) => ({
    ...state,
    agentSection: { ...state.agentSection, premiumPercentIncrease },
  })),
  on(processActions.selectPackageAddons, (state, { addon, value }) => ({
    ...state,
    packageAddons: {
      state: {
        ...state.packageAddons.state,
        [addon]: value,
      },
    },
  })),
  on(processActions.usePolicyDataStart, (state) => ({
    ...state,
    usePolicyDataState: ApiState.pending,
  })),
  on(processActions.usePolicyDataSuccess, (state, { success }) => ({
    ...state,
    usePolicyDataState: ApiState.success,
    usePolicyData: success,
    usePolicyDataError: null,
  })),
  on(processActions.usePolicyDataError, (state, { error }) => ({
    ...state,
    usePolicyDataState: ApiState.error,
    usePolicyDataError: error,
  })),
  on(processActions.usePolicyDataReset, (state) => ({
    ...state,
    usePolicyDataState: ApiState.idle,
    usePolicyDataError: null,
    usePolicyData: null,
  })),
  on(processActions.setIsOfferPriceBoxVisible, (state, { isOfferPriceBoxVisible }) => ({
    ...state,
    isOfferPriceBoxVisible,
  })),

  on(processActions.knfLimitStart, (state, { payload }) => ({
    ...state,
    knfLimitState: ApiState.pending,
    knfLimitError: null,
    knfLimitPayload: payload,
  })),
  on(processActions.knfLimitSuccess, (state, { success }) => ({
    ...state,
    knfLimitState: ApiState.success,
    knfLimit: success,
    knfLimitError: null,
  })),
  on(processActions.saveCalculationError, (state, { error }) => ({
    ...state,
    knfLimitState: ApiState.error,
    knfLimit: null,
    knfLimitError: error,
    knfLimitPayload: null,
  })),
  on(processActions.blockListCheckStart, (state) => ({
    ...state,
    blockListCheckState: ApiState.pending,
    blockListCheckError: null,
  })),
  on(processActions.blockListCheckSuccess, (state, { success }) => ({
    ...state,
    blockListCheckState: ApiState.success,
    blockListCheck: success,
    blockListCheckError: null,
  })),
  on(processActions.blockListCheckError, (state, { error }) => ({
    ...state,
    blockListCheckState: ApiState.error,
    blockListCheck: null,
    blockListCheckError: error,
  })),
);
